import { useState, useEffect } from 'react';
import { useSEO, generateWebPageSchema, generateBreadcrumbSchema } from '../../utils/seo';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import RelocationHero from './components/RelocationHero';
import OverviewSection from './components/OverviewSection';
import CostOfLivingSection from './components/CostOfLivingSection';
import NeighborhoodsSection from './components/NeighborhoodsSection';
import SchoolsSection from './components/SchoolsSection';
import UtilitiesSection from './components/UtilitiesSection';
import DMVChecklistSection from './components/DMVChecklistSection';
import First30DaysSection from './components/First30DaysSection';
import LocalTipsSection from './components/LocalTipsSection';
import ContactSection from './components/ContactSection';
import MailchimpNewsletter from '../../components/feature/MailchimpNewsletter';

export default function RelocationPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useSEO({
    title: 'Moving to Roseville California - Complete 2025 Relocation Guide | Roseville.org',
    description: 'Planning on moving to Roseville CA? Complete relocation guide with neighborhoods, cost of living, schools, utilities, DMV checklist, and local tips. Everything you need to know about relocating to Roseville California.',
    keywords: 'moving to Roseville California, relocating to Roseville CA, Roseville relocation guide, moving to Roseville 2025, cost of living Roseville, Roseville neighborhoods, Roseville schools, moving checklist Roseville',
    ogImage: 'https://public.readdy.ai/ai/img_res/e1945ec11b1f0729b5e56beb3e093277.jpg',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          name: 'Moving to Roseville California - Complete 2025 Relocation Guide',
          description: 'Comprehensive relocation guide for people moving to Roseville, California',
          url: `${import.meta.env.VITE_SITE_URL || 'https://roseville.org'}/relocation`,
          inLanguage: 'en-US',
          isPartOf: { '@type': 'WebSite', name: 'Roseville.org', url: import.meta.env.VITE_SITE_URL || 'https://roseville.org' },
          dateModified: '2025-07-15',
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${import.meta.env.VITE_SITE_URL || 'https://roseville.org'}/` },
            { '@type': 'ListItem', position: 2, name: 'Relocation Guide', item: `${import.meta.env.VITE_SITE_URL || 'https://roseville.org'}/relocation` },
          ],
        },
        {
          '@type': 'HowTo',
          name: 'How to Relocate to Roseville, California',
          description: 'Step-by-step guide for moving to Roseville CA, covering neighborhoods, schools, utilities, and DMV registration.',
          step: [
            { '@type': 'HowToStep', position: 1, name: 'Research Neighborhoods', text: 'Explore Roseville neighborhoods to find the best fit for your lifestyle and budget.' },
            { '@type': 'HowToStep', position: 2, name: 'Review Cost of Living', text: 'Compare housing costs, utilities, and everyday expenses in Roseville, CA.' },
            { '@type': 'HowToStep', position: 3, name: 'Enroll Children in Schools', text: 'Contact the Roseville City School District or RJUHSD to enroll your children.' },
            { '@type': 'HowToStep', position: 4, name: 'Set Up Utilities', text: 'Contact Roseville Electric, Water, and Wastewater to establish service at your new address.' },
            { '@type': 'HowToStep', position: 5, name: 'Update DMV Registration', text: 'Visit the California DMV within 10 days of establishing residency to update your license and vehicle registration.' },
          ],
        },
      ],
    },
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar isScrolled={isScrolled} />
      <RelocationHero />
      <OverviewSection />
      <CostOfLivingSection />
      <NeighborhoodsSection />
      <SchoolsSection />
      <UtilitiesSection />
      <DMVChecklistSection />
      <First30DaysSection />
      <LocalTipsSection />
      <ContactSection />

      {/* Featured Partners Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <i className="ri-star-fill"></i>
              <span>Trusted Local Partners</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Connect With Relocation Experts
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Work with verified real estate agents, moving companies, mortgage brokers, and apartment communities to make your move seamless.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6 mb-12">
            <div className="bg-white rounded-xl p-6 text-center shadow-md hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-home-smile-line text-3xl text-emerald-600"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Real Estate Agents</h3>
              <p className="text-gray-600 text-sm mb-4">Expert agents with deep local market knowledge</p>
              <div className="text-2xl font-bold text-emerald-600">15+</div>
            </div>

            <div className="bg-white rounded-xl p-6 text-center shadow-md hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-truck-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Moving Companies</h3>
              <p className="text-gray-600 text-sm mb-4">Licensed and insured professional movers</p>
              <div className="text-2xl font-bold text-teal-600">8+</div>
            </div>

            <div className="bg-white rounded-xl p-6 text-center shadow-md hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-bank-line text-3xl text-cyan-600"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Mortgage Brokers</h3>
              <p className="text-gray-600 text-sm mb-4">Competitive rates and fast pre-approvals</p>
              <div className="text-2xl font-bold text-cyan-600">12+</div>
            </div>

            <div className="bg-white rounded-xl p-6 text-center shadow-md hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-building-line text-3xl text-purple-600"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Apartments</h3>
              <p className="text-gray-600 text-sm mb-4">Premium communities with great amenities</p>
              <div className="text-2xl font-bold text-purple-600">20+</div>
            </div>
          </div>

          <div className="text-center">
            <a
              href="/relocation-partners"
              className="inline-flex items-center gap-2 bg-emerald-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-emerald-700 transition-colors whitespace-nowrap text-lg"
            >
              <span>View All Partners</span>
              <i className="ri-arrow-right-line text-xl"></i>
            </a>
          </div>
        </div>
      </section>

      <MailchimpNewsletter />
      <Footer />
    </div>
  );
}
